import { Checkbox, Skeleton } from '@heroui/react'
import { RefundMethod, RefundPaymentMethod } from '@itsrever/returns-api-types'
import { usePostHog } from '@posthog/react'
import { skipToken, useQuery } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/hooks'
import { LineItemSubtypes } from '@/redux/return/return-slice'
import { returnsApi } from '@/redux/api/returns-api-slice'
import { useSummaryData } from '@/hooks/use-summary'

interface GiftCardOfferProps {
  selected: boolean
  onSelectedChange: (selected: boolean) => void
  giftCardMethod: RefundMethod
}

export const GiftCardOffer = ({
  selected,
  onSelectedChange,
  giftCardMethod
}: GiftCardOfferProps) => {
  const { t, i18n } = useTranslation()

  const posthog = usePostHog()

  const order = useAppSelector((store) => store.returnsApi.order.response)

  const orderMoneyFormat = order.order?.money_format

  const {
    ecommerceID,
    calculateData: { checkout_id, ...calculateData }
  } = useSummaryData()

  const { data, status } = useQuery({
    queryKey: ['gift-card-calculated-summary'],
    queryFn:
      ecommerceID && checkout_id
        ? async () => {
            const { data } = await returnsApi.calculate({
              ecommerceID,
              calculateData: {
                ...calculateData,
                refund_method: giftCardMethod.method,
                checkout_id: checkout_id
              }
            })

            return data
          }
        : skipToken,
    placeholderData: (data) => data,
    staleTime: 0
  })

  // The same offer covers both compensations, so the copy follows the method the
  // merchant actually issues instead of always naming a gift card.
  const isPromoCode =
    giftCardMethod.method === RefundPaymentMethod.RefundPaymentMethod_PROMO_CODE

  const offerLabelKey = isPromoCode
    ? 'confirmation.promo_code_offer_label'
    : 'confirmation.gift_card_offer_label'

  const offerDescriptionKey = isPromoCode
    ? 'confirmation.promo_code_offer_description'
    : 'confirmation.gift_card_offer_description'

  const refundAmount =
    data?.signed_summary?.return_summary?.total_refund_amount ?? 0

  const shippingCost = data?.calculated_summary?.costs?.find(
    (cost) => cost.subtype === LineItemSubtypes.ShippingCost
  )

  const hasFreeShipping = !shippingCost || shippingCost.total === 0

  const formattedRefund = (
    refundAmount / (orderMoneyFormat?.amount_multiplied_by ?? 100)
  ).toLocaleString(i18n.language, {
    style: 'currency',
    currency: orderMoneyFormat?.currency
  })

  if (status === 'pending') {
    return <Skeleton className="h-[172px] w-full rounded-md" />
  }

  return status === 'success' ? (
    <div className="relative rounded-md border-2 border-solid border-blue-400 bg-gradient-to-br from-blue-200/50 via-blue-100/40 to-blue-50/20 py-3 pl-3 pr-3.5 shadow-[0_0_6px_0_rgba(59,130,246,0.35)]">
      <Checkbox
        color="primary"
        radius="sm"
        isSelected={selected}
        onValueChange={(checked) => {
          onSelectedChange(checked)

          posthog.capture('Gift Card Offer Selected', {
            checked,
            checkout_id: order.checkout_id
          })
        }}
        classNames={{
          base: 'm-0 p-0 w-full static before:absolute before:inset-0 before:size-full gap-1',
          wrapper:
            'bg-white group-data-[selected=true]:before:bg-neutral-800 group-data-[selected=true]:before:border-neutral-800'
        }}
      >
        <p className="flex items-center text-base font-semibold text-neutral-900">
          {t(offerLabelKey)}
        </p>
      </Checkbox>
      <p className="pl-8 pt-0.5 text-sm font-medium text-neutral-800 [text-wrap:balance]">
        {t(offerDescriptionKey)}
      </p>
      <ul className="mt-2 flex list-disc flex-col flex-wrap gap-1.5 pl-11">
        <li className="text-sm font-medium text-neutral-900">
          {t('confirmation.gift_card_service_fee')}
        </li>
        {hasFreeShipping ? (
          <li className="text-sm font-medium text-neutral-900">
            {t('confirmation.gift_card_free_shipping')}
          </li>
        ) : null}
        <li className="-mt-0.5 text-sm font-medium text-neutral-900">
          <Trans
            i18nKey="confirmation.gift_card_total_refund"
            values={{ totalRefundAmount: formattedRefund }}
            components={{
              span: (
                <span className="whitespace-nowrap text-base font-semibold text-[#1790FF]" />
              )
            }}
          />
        </li>
      </ul>
    </div>
  ) : null
}
